import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import Helmet from 'react-helmet';
import cx from 'classnames';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { RenderField, ButtonV2, Link } from '@zola/zola-ui';

import ButtonIconSquare from '../../../common/zolaUI/ButtonIconSquare/ButtonIconSquare';

import * as validations from '../../../common/form/commonValidations';
import ModalFullscreen from '../../../common/zolaUI/ModalFullscreen/ModalFullscreen';
import { notDesktop, isDesktop } from '../../../../lib/responsive';
import renderIf from '../../../../util/renderIf';

import './ChecklistInvitePartner.less';

class ChecklistInvitePartner extends React.Component {
  constructor() {
    super();

    this.state = {
      recaptchaReady: this.isRecaptchaReady(),
      recaptchaValid: null,
      recaptchaExpired: false,
    };

    this.wrapperRef = React.createRef();
    this.reacaptchaRef = React.createRef();
    this.recaptchaCheck = null;
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    if (this.reacaptchaRef.current) this.initializeRecaptcha();
  }

  componentDidUpdate(prevProps) {
    if (!isEmpty(prevProps.partnerStatus) && isEmpty(this.props.partnerStatus))
      this.initializeRecaptcha();
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    if (this.recaptchaCheck) clearInterval(this.recaptchaCheck);
  }

  isRecaptchaReady = () =>
    typeof window !== 'undefined' && typeof window.grecaptcha !== 'undefined';

  initializeRecaptcha = () => {
    this.recaptchaCheck = setInterval(() => {
      if (this.state.recaptchaReady && this.reacaptchaRef.current) {
        const sitekey =
          window.zola.env === 'production'
            ? '6LeioV0UAAAAAPRmqrC2cERXl8k001P_zm7lqcKA'
            : '6Le-kV0UAAAAAPJLDAI2TeBQEuy0oCpRA4mZg8am';
        try {
          window.grecaptcha.render('invite-recaptcha', {
            sitekey,
            callback: this.recaptchaCallback,
            'expired-callback': this.onRecaptchaExpired,
          });
        } catch (error) {
          // mostly likely already loaded into element
          clearInterval(this.recaptchaCheck);
        }
        clearInterval(this.recaptchaCheck);
      } else {
        this.setState({ recaptchaReady: this.isRecaptchaReady() });
      }
    }, 500);
  };

  recaptchaCallback = res => {
    this.setState({ recaptchaValid: res });
  };

  onRecaptchaExpired = () => {
    this.setState({ recaptchaExpired: true });
  };

  handleClickOutside = event => {
    const isClickOutside =
      this.wrapperRef.current && !this.wrapperRef.current.contains(event.target);

    if (isClickOutside && isDesktop()) {
      const { isActive, toggleDropdown } = this.props;
      if (isActive) {
        toggleDropdown();
      }
    }
  };

  // eslint-disable-next-line camelcase
  handleFormSubmit = ({ to_email }) => {
    const { onSubmit, userContext } = this.props;
    const { object_id: from_user_id } = userContext; // eslint-disable-line camelcase
    const finalReq = { from_user_id, to_email };
    return onSubmit(finalReq);
  };

  renderForm = () => {
    const { handleSubmit, valid, toggleDropdown, isActive } = this.props;
    const { recaptchaExpired, recaptchaValid } = this.state;
    const formIsValid = valid && recaptchaValid && !recaptchaExpired;

    return (
      <form onSubmit={handleSubmit(this.handleFormSubmit)}>
        {isActive && ( // helps autoFocus work by popping off the DOM when closed
          <Field
            name="to_email"
            id="invite-partner__email"
            type="email"
            label="Email"
            className="invite-partner__email"
            required
            autoFocus
            component={RenderField}
            validate={[validations.REQUIRED, validations.EMAIL]}
          />
        )}
        <div
          ref={this.reacaptchaRef}
          className="g-recaptcha invite-partner__recaptcha"
          id="invite-recaptcha"
        />
        <ButtonV2
          className="invite-partner__submit"
          onSubmit={handleSubmit(this.handleFormSubmit)}
          type="submit"
          disabled={!formIsValid}
        >
          Send Invite
        </ButtonV2>
        <div className="invite-partner__cancel">
          <Link onClick={toggleDropdown} className="checklist__button--cancel">
            Cancel
          </Link>
        </div>
      </form>
    );
  };

  renderPartnerInfo = () => {
    const { partnerStatus, showConfirmationModal, partnerFirstName, partnerLastName } = this.props;
    return (
      <div className="invite-partner__partner-exists">
        <div className="invite-partner__partner-info">
          <div className="invite-partner__name">
            {partnerFirstName} {partnerLastName}
          </div>
          <div className="invite-partner__sublabel">
            Invitation {partnerStatus.acceptedAt ? 'accepted' : 'sent'} on{' '}
            {moment(partnerStatus.acceptedAt || partnerStatus.createdAt).format('ll')}
          </div>
        </div>
        <button
          className="invite-partner__remove checklist__button"
          onClick={showConfirmationModal}
        >
          <i className="zola-ui-icon zola-ui-icon-trash checklist__deleteTask--icon" />
          <span className="invite-partner__remove--label">Remove</span>
        </button>
      </div>
    );
  };

  renderDropdown = () => {
    const { partnerStatus, toggleDropdown, isActive } = this.props;

    if (notDesktop()) {
      return (
        <ModalFullscreen
          headerTitle="Invite Partner"
          onClose={toggleDropdown}
          className={cx('checklist__fullScreenModal', 'invite-partner__fullScreenModal', {
            'checklist__fullScreenModal--is_open': isActive,
          })}
        >
          <p className="invite-partner__subtext">
            Your invited partner can manage and edit your Zola wedding planning tools.
          </p>
          {renderIf(partnerStatus.status, this.renderPartnerInfo, this.renderForm)}
        </ModalFullscreen>
      );
    }

    return (
      <div
        className={cx('invite-partner__dropdown', {
          'invite-partner__dropdown--is_open': isActive,
        })}
      >
        <h2 className="invite-partner__title">Invite Partner</h2>
        <p className="invite-partner__subtext">
          Your invited partner can manage and edit your Zola wedding planning tools.
        </p>
        {renderIf(partnerStatus.status, this.renderPartnerInfo, this.renderForm)}
      </div>
    );
  };

  render() {
    const { toggleDropdown, renderIconButton } = this.props;
    return (
      <div ref={this.wrapperRef} className="invite-partner__container checklist__nav--leftButtons">
        <Helmet>
          <script
            src="https://www.google.com/recaptcha/api.js?initializeRecaptcha=initializeRecaptcha&render=explicit"
            async
            defer
          />
        </Helmet>
        {renderIconButton && (
          <ButtonIconSquare
            onClick={toggleDropdown}
            iconClassName="zola-ui-icon-invite-partner"
            secondary
            className="checklist__nav--invitePartner"
          />
        )}
        {this.renderDropdown()}
      </div>
    );
  }
}

ChecklistInvitePartner.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  partnerStatus: PropTypes.shape({}),
  isActive: PropTypes.bool,
  toggleDropdown: PropTypes.func,
  renderIconButton: PropTypes.bool,
  partnerFirstName: PropTypes.string,
  partnerLastName: PropTypes.string,
};

ChecklistInvitePartner.defaultProps = {
  partnerStatus: {},
};

export default ChecklistInvitePartner;



// WEBPACK FOOTER //
// ./src/components/manage/ChecklistV2/ChecklistInvitePartner/ChecklistInvitePartner.jsx